import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Bed, Bath, Maximize, MapPin, ArrowLeft, Phone, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { getPropertyBySlug, getSiteSettings } from '../services/api';
import { AppointmentBooking } from '../components/AppointmentBooking';
import SEO from '../components/SEO';

const PropertyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [siteSettings, setSiteSettings] = useState({
    phone: '(434) 555-0123',
    email: 'info@cozyproperties.com'
  });

  useEffect(() => {
    const loadProperty = async () => {
      try {
        const data = await getPropertyBySlug(slug);
        setProperty(data);
      } catch (error) {
        console.error('Failed to load property:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProperty();
  }, [slug]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getSiteSettings();
        setSiteSettings(settings);
      } catch (error) {
        console.error('Failed to load site settings:', error);
      }
    };
    loadSettings();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <p className="text-xl text-gray-600 dark:text-gray-400">Loading property...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">Property not found</p>
          <Button onClick={() => navigate('/listings')}>Back to Listings</Button>
        </div>
      </div>
    );
  }

  const allImages = [property.image, ...(property.images || [])];
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handlePrevImage = () => {
    setSelectedImage((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImage((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  // Helper function to strip HTML tags for meta description
  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Generate structured data for property
  const structuredData = property ? {
    "@context": "https://schema.org",
    "@type": "Apartment",
    "name": property.title,
    "description": stripHtml(property.description).substring(0, 200),
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Lynchburg",
      "addressRegion": "VA",
      "streetAddress": property.address
    },
    "numberOfRooms": property.bedrooms,
    "numberOfBathroomsTotal": property.bathrooms,
    "floorSize": {
      "@type": "QuantitativeValue",
      "value": property.sqft,
      "unitCode": "FTK"
    },
    "image": allImages[0],
    "offers": {
      "@type": "Offer",
      "price": property.price,
      "priceCurrency": "USD",
      "availability": property.status === 'available' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
    }
  } : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-24 pb-8 overflow-x-hidden">
      {property && (
        <SEO 
          title={property.title}
          description={stripHtml(property.description).substring(0, 160)}
          keywords={`${property.title}, ${property.address}, ${property.bedrooms} bedroom rental Lynchburg, ${property.bathrooms} bath apartment, rental home Lynchburg VA`}
          image={allImages[0]}
          type="product"
          structuredData={structuredData}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/listings')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Listings
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Image Gallery */}
            <Card className="overflow-hidden dark:bg-gray-900 dark:border-gray-800 w-full">
              <div className="relative h-64 sm:h-80 md:h-96 bg-gray-100 dark:bg-gray-800 group flex items-center justify-center">
                <img
                  src={allImages[selectedImage].startsWith('http') 
                    ? allImages[selectedImage]
                    : `${BACKEND_URL}${allImages[selectedImage]}`}
                  alt={property.title}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    console.error('Image failed to load:', allImages[selectedImage]);
                  }}
                />
                <div className="absolute top-4 right-4">
                  <Badge className={`text-white ${
                    property.status === 'available' ? 'bg-otter-green hover:bg-otter-green' : 
                    property.status === 'coming_soon' ? 'bg-otter-teal hover:bg-otter-teal' : 
                    'bg-gray-500 hover:bg-gray-500'
                  }`}>
                    {property.status === 'available' ? 'Available' : 
                     property.status === 'coming_soon' ? 'Coming Soon' : 
                     'Rented'}
                  </Badge>
                </div>
                
                {/* Navigation Buttons - Only show if more than 1 image */}
                {allImages.length > 1 && (
                  <>
                    {/* Previous Button */}
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    
                    {/* Next Button */}
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
                      aria-label="Next image"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                    
                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {selectedImage + 1} / {allImages.length}
                    </div>
                  </>
                )}
              </div>
              
              {/* Thumbnail Carousel */}
              {allImages.length > 1 && (
                <div className="p-4">
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                    {allImages.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`relative h-20 w-28 flex-shrink-0 rounded overflow-hidden border-2 transition-all ${
                          selectedImage === index 
                            ? 'border-blue-700 ring-2 ring-blue-700' 
                            : 'border-gray-300 dark:border-gray-700 hover:border-otter-green'
                        }`}
                      >
                        <img
                          src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`}
                          alt={`View ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Thumbnail failed to load:', img);
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Property Details */}
            <Card className="dark:bg-gray-900 dark:border-gray-800 w-full">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {property.title}
                    </h1>
                    <div className="flex items-center gap-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                      <MapPin className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                      <span className="break-words">{property.address}</span>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    <div className="text-3xl sm:text-4xl font-bold text-otter-green dark:text-otter-green-400">
                      ${property.price}
                      <span className="text-base sm:text-lg font-normal text-gray-600 dark:text-gray-400">/mo</span>
                    </div>
                  </div>
                </div>

                {/* Property Features */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4 py-4 sm:py-6 border-y border-gray-200 dark:border-gray-700 my-4 sm:my-6">
                  <div className="text-center">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 mb-1">
                      <Bed className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 dark:text-gray-400" />
                      <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{property.bedrooms}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Bedrooms</p>
                  </div>
                  <div className="text-center">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 mb-1">
                      <Bath className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 dark:text-gray-400" />
                      <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{property.bathrooms}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Bathrooms</p>
                  </div>
                  <div className="text-center">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 mb-1">
                      <Maximize className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 dark:text-gray-400" />
                      <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{property.sqft}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Sq Ft</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Description</h2>
                  <div 
                    className="text-gray-600 dark:text-gray-400 leading-relaxed prose dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: property.description }}
                  />
                </div>

                {/* Pet Policy Section - Only show if pet_policy exists */}
                {property.pet_policy && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Pet Policy</h2>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <span className="font-semibold text-gray-900 dark:text-white min-w-[120px]">Status:</span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {property.pets_allowed === 'no' && 'No Pets Allowed'}
                          {property.pets_allowed === 'yes' && 'All Pets Welcome'}
                          {property.pets_allowed === 'cats_only' && 'Cats Only'}
                          {property.pets_allowed === 'dogs_only' && 'Dogs Only'}
                          {property.pets_allowed === 'case_by_case' && 'Pets Considered Case by Case'}
                        </span>
                      </div>
                      {property.pet_deposit > 0 && (
                        <div className="flex items-start gap-3">
                          <span className="font-semibold text-gray-900 dark:text-white min-w-[120px]">Pet Deposit:</span>
                          <span className="text-gray-600 dark:text-gray-400">${property.pet_deposit}</span>
                        </div>
                      )}
                      {property.pet_rent > 0 && (
                        <div className="flex items-start gap-3">
                          <span className="font-semibold text-gray-900 dark:text-white min-w-[120px]">Monthly Pet Rent:</span>
                          <span className="text-gray-600 dark:text-gray-400">${property.pet_rent}/mo</span>
                        </div>
                      )}
                      <div className="flex items-start gap-3">
                        <span className="font-semibold text-gray-900 dark:text-white min-w-[120px]">Details:</span>
                        <p className="text-gray-600 dark:text-gray-400 flex-1 whitespace-pre-line">
                          {property.pet_policy}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 min-w-0">
            {/* Contact Card */}
            <Card className="dark:bg-gray-900 dark:border-gray-800">
              <CardContent className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">Interested in this property?</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Contact us to schedule a viewing or learn more about this property.
                </p>
                <div className="space-y-3">
                  <a href={`tel:${siteSettings.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <Phone className="h-5 w-5 text-otter-green dark:text-otter-green-400" />
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Call or Text Us</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{siteSettings.phone}</p>
                    </div>
                  </a>
                  <a href={`mailto:${siteSettings.email}`} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <Mail className="h-5 w-5 text-otter-green dark:text-otter-green-400" />
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Email us</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{siteSettings.email}</p>
                    </div>
                  </a>
                </div>
                {property.status === 'available' && property.applicationLink && (
                  <div className="mt-4">
                    <a 
                      href={property.applicationLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button className="w-full bg-otter-teal hover:bg-otter-teal/90 text-white border-2 border-otter-teal">
                        Apply Now
                      </Button>
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Schedule Appointment */}
            {property.status === 'available' && (
              <AppointmentBooking 
                propertyId={property.id} 
                propertyTitle={property.title}
              />
            )}

            {/* Requirements Card */}
            <Card className="dark:bg-gray-900 dark:border-gray-800">
              <CardContent className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">Application Requirements</h3>
                {property.application_requirements ? (
                  <div 
                    className="text-sm text-gray-600 dark:text-gray-400 space-y-2"
                    dangerouslySetInnerHTML={{ __html: property.application_requirements }}
                  />
                ) : (
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-2">
                      <span className="text-otter-green dark:text-otter-green-400 mt-1">•</span>
                      <span>Income 3x monthly rent</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-otter-green dark:text-otter-green-400 mt-1">•</span>
                      <span>Credit score 600+</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-otter-green dark:text-otter-green-400 mt-1">•</span>
                      <span>Positive rental history</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-otter-green dark:text-otter-green-400 mt-1">•</span>
                      <span>Background check required</span>
                    </li>
                  </ul>
                )}
                <Link to="/qualifications">
                  <Button variant="outline" className="w-full mt-4">
                    View Full Requirements
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
